<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useCommunicationErrorMessage } from 'dashboard/composables/useCommunicationErrorMessage';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const { friendlyMessage } = useCommunicationErrorMessage();

const connection = computed(
  () => getters['communicationConnection/getCommunicationConnection'].value
);
const accounts = computed(
  () => getters['communicationConnection/getAccounts'].value
);
const uiFlags = computed(
  () => getters['communicationConnection/getUIFlags'].value
);

const isConfigured = computed(
  () => !!connection.value?.id && connection.value?.enabled
);
const selectedGatewayAccountId = ref('');
const inboxName = ref('');
const bindError = ref('');

const isBindable = account =>
  account.connection_status === 'open' && !!account.phone_number;

const selectAccount = account => {
  if (!isBindable(account)) return;
  selectedGatewayAccountId.value = account.gateway_account_id;
  if (!inboxName.value) {
    inboxName.value = account.gateway_instance_name || account.phone_number;
  }
};

const loadAccounts = async () => {
  try {
    await store.dispatch('communicationConnection/fetchAccounts');
  } catch (error) {
    useAlert(friendlyMessage(error));
  }
};

onMounted(async () => {
  await store.dispatch('communicationConnection/get');
  if (isConfigured.value) {
    await loadAccounts();
  }
});

const createInbox = async () => {
  bindError.value = '';
  if (!selectedGatewayAccountId.value) return;

  try {
    const inbox = await store.dispatch('communicationConnection/bind', {
      gatewayAccountId: selectedGatewayAccountId.value,
      inboxName: inboxName.value,
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    bindError.value = friendlyMessage(error);
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="uiFlags.isFetching" class="text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.LOADING') }}
    </div>

    <div
      v-else-if="!isConfigured"
      class="p-4 rounded-lg border border-n-strong text-sm text-n-slate-12"
    >
      {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.NOT_CONFIGURED') }}
      <router-link
        :to="{ name: 'communication_connection_index' }"
        class="text-n-blue-11 hover:underline"
      >
        {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.CONFIGURE_LINK') }}
      </router-link>
    </div>

    <template v-else>
      <div>
        <h4 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.ACCOUNTS_TITLE') }}
        </h4>

        <div v-if="uiFlags.isFetchingAccounts" class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.ACCOUNTS_LOADING') }}
        </div>
        <p v-else-if="accounts.length === 0" class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.ACCOUNTS_EMPTY') }}
        </p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-1 pr-4" />
              <th class="py-1 pr-4">
                {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.TABLE.NAME') }}
              </th>
              <th class="py-1 pr-4">
                {{
                  t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.TABLE.NUMBER')
                }}
              </th>
              <th class="py-1 pr-4">
                {{
                  t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.TABLE.STATUS')
                }}
              </th>
              <th class="py-1">
                {{
                  t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.TABLE.LAST_SEEN')
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="account in accounts"
              :key="account.gateway_account_id"
              class="cursor-pointer"
              :class="[
                isBindable(account)
                  ? 'text-n-slate-12'
                  : 'text-n-slate-9 cursor-not-allowed',
                selectedGatewayAccountId === account.gateway_account_id
                  ? 'bg-n-alpha-2'
                  : '',
              ]"
              @click="selectAccount(account)"
            >
              <td class="py-1 pr-4">
                <input
                  type="radio"
                  :checked="
                    selectedGatewayAccountId === account.gateway_account_id
                  "
                  :disabled="!isBindable(account)"
                />
              </td>
              <td class="py-1 pr-4">{{ account.gateway_instance_name }}</td>
              <td class="py-1 pr-4">{{ account.phone_number || '—' }}</td>
              <td class="py-1 pr-4">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="
                    account.connection_status === 'open'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-ruby-3 text-n-ruby-11'
                  "
                >
                  {{ account.connection_status }}
                </span>
              </td>
              <td class="py-1">
                {{
                  account.last_seen_at
                    ? messageTimestamp(account.last_seen_at)
                    : '—'
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <form
        v-if="selectedGatewayAccountId"
        class="flex flex-col gap-4 max-w-xl"
        @submit.prevent="createInbox"
      >
        <Input
          v-model="inboxName"
          :label="
            t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.INBOX_NAME.LABEL')
          "
          :placeholder="
            t(
              'INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.INBOX_NAME.PLACEHOLDER'
            )
          "
        />

        <p v-if="bindError" class="text-n-ruby-11 text-sm">{{ bindError }}</p>

        <div>
          <NextButton
            type="submit"
            solid
            blue
            :is-loading="uiFlags.isBinding"
            :label="
              t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_GATEWAY.SUBMIT_BUTTON')
            "
          />
        </div>
      </form>
    </template>
  </div>
</template>
